import i18n from 'i18next';
import { get } from 'lodash/fp';
import accountsService from 'additiv-services/services/core/accounts';
import contactManagement from 'additiv-services/services/core/contactManagement';
import instrumentsService from 'additiv-services/services/core/instruments';
import PortfolioManagement from 'additiv-services/services/core/portfolioManagement';
import performanceService from 'additiv-services/services/core/performance';
import Products from 'additiv-services/services/core/products';
import Security, { security } from 'additiv-services/services/core/security';
import commonService from 'additiv-services/services/core/common';
import commonContent from 'additiv-services/services/core/commonContent';
import currenciesService from 'additiv-services/services/core/currencies';
import complianceService from 'additiv-services/services/core/compliance';
import dataService from 'additiv-services/services/core/data';
import messageService from 'additiv-services/services/core/messages';
import contactInteraction from 'additiv-services/services/core/contactInteraction';
import documents from 'additiv-services/services/core/documents';
import memberManagement from 'additiv-services/services/core/memberManagement';
import clientService from 'additiv-services/services/core/clientService';
import Service from 'additiv-services/services/core/service';
import engagementSchema from 'additiv-services/services/core/engagementSchema';
import engagementContent from 'additiv-services/services/core/engagementContent';
import fIdentity from 'additiv-services/services/core/fIdentity';
import middleware from 'additiv-services/services/core/middleware';
import Definitions from 'additiv-services/services/core/definitions';
import { setup } from 'additiv-services';
import bank from './services/bank';
import member from './services/member';
import { PortfolioManagement as PortfolioManagementLocal } from './services/portfolio-management';
import { ContactIdentityServices } from './services/contactIdentityServices';
import customService from './custom/CustomService';
import documentService from './documents/DocumentService';
import { checkStatus } from './generalStatusCheck';
import CustomClientServices from './custom/CustomClientServices';
import customDocumentService from './custom/CustomDocumentService';
import PlanService from './custom/PlanService';
import CustomMembersService from './custom/CustomMembersService';
import CustomReportsService from './custom/CustomReports';
import CustomInvestmentService from './custom/CustomInvestmentService';
import ContactActivation from './custom/ContactActivation';
import CustomCommonService from './custom/CustomCommonService';
import CustomAdvisoryServices from './custom/CustomAdvisoryServices';
import CustomCaseManagementServices from './custom/CustomCaseManagementServices';
import CustomSystemMessageServices from './custom/CustomSystemMessageServices';

const serviceCall = (service: Record<string, (...args: unknown[]) => unknown>, method: string, argsArray: unknown[] = []) => {
    const result = service[method](...argsArray);
    const promise = result instanceof Promise ? result : (result as [Promise<unknown>])[0];

    promise
        .then(() => {})
        .catch((reason) => {
            checkStatus(get('response.status', reason));
        });

    return result;
};

class ServiceManager {
    static Service(method: string, argsArray: unknown[]) { return serviceCall(Service, method, argsArray); }

    static compliance(method: string, argsArray: unknown[]) { return serviceCall(complianceService, method, argsArray); }

    static contactManagement(method: string, argsArray: unknown[]) { return serviceCall(contactManagement, method, argsArray); }

    static dataService(method: string, argsArray: unknown[]) { return serviceCall(dataService, method, argsArray); }

    static performanceService(method: string, argsArray: unknown[]) { return serviceCall(performanceService, method, argsArray); }

    static instrumentsService(method: string, argsArray: unknown[]) { return serviceCall(instrumentsService, method, argsArray); }

    static portfolioManagement(method: string, argsArray: unknown[]) { return serviceCall(PortfolioManagement, method, argsArray); }

    static portfolioManagementLocal(method: string, argsArray: unknown[]) { return serviceCall(PortfolioManagementLocal, method, argsArray); }

    static contactIdentityServices(method: string, argsArray: unknown[]) { return serviceCall(ContactIdentityServices, method, argsArray); }

    static products(method: string, argsArray: unknown[]) { return serviceCall(Products, method, argsArray); }

    static security(method: string, argsArray: unknown[]) { return serviceCall(security, method, argsArray); }

    static Security(method: string, argsArray: unknown[]) { return serviceCall(Security, method, argsArray); }

    static commonService(method: string, argsArray: unknown[]) { return serviceCall(commonService, method, argsArray); }

    static currenciesService(method: string, argsArray: unknown[]) { return serviceCall(currenciesService, method, argsArray); }

    static commonContent(method: string, argsArray: unknown[]) { return serviceCall(commonContent, method, argsArray); }

    static messageService(method: string, argsArray: unknown[]) { return serviceCall(messageService, method, argsArray); }

    static accountsService(method: string, argsArray: unknown[]) { return serviceCall(accountsService, method, argsArray); }

    static bank(method: string, argsArray: unknown[]) { return serviceCall(bank, method, argsArray); }

    static member(method: string, argsArray: unknown[]) { return serviceCall(member, method, argsArray); }

    static memberManagement(method: string, argsArray: unknown[]) { return serviceCall(memberManagement, method, argsArray); }

    static documents(method: string, argsArray: unknown[]) { return serviceCall(documents, method, argsArray); }

    static engagementSchema(method: string, argsArray: unknown[]) { return serviceCall(engagementSchema, method, argsArray); }

    static engagementContent(method: string, argsArray: unknown[]) { return serviceCall(engagementContent, method, argsArray); }

    static fIdentity(method: string, argsArray: unknown[]) { return serviceCall(fIdentity, method, argsArray); }

    static middleware(method: string, argsArray: unknown[]) { return serviceCall(middleware, method, argsArray); }

    static definitions(method: string, argsArray: unknown[]) { return serviceCall(Definitions, method, argsArray); }

    static documentService(method: string, argsArray: unknown[]) { return serviceCall(documentService, method, argsArray); }

    static contactActivation(method: string, argsArray: unknown[]) {
        setup({ CUSTOM_SERVICE_LAYER_HEADERS: { 'accept-language': i18n.language } });

        return serviceCall(ContactActivation, method, argsArray);
    }

    static customService(method: string, argsArray: unknown[]) {
        setup({ CUSTOM_SERVICE_LAYER_HEADERS: { 'accept-language': i18n.language } });

        return serviceCall(customService, method, argsArray);
    }

    static customDocumentsService(method: string, argsArray: unknown[]) {
        setup({ CUSTOM_SERVICE_LAYER_HEADERS: { 'accept-language': i18n.language } });

        return serviceCall(customDocumentService, method, argsArray);
    }

    static customClientService(method: string, argsArray: unknown[]) {
        setup({ CUSTOM_SERVICE_LAYER_HEADERS: { 'accept-language': i18n.language } });

        return serviceCall(CustomClientServices, method, argsArray);
    }

    static customMembersService(method: string, argsArray: unknown[]) {
        setup({ CUSTOM_SERVICE_LAYER_HEADERS: { 'accept-language': i18n.language } });

        return serviceCall(CustomMembersService, method, argsArray);
    }

    static planService(method: string, argsArray: unknown[]) {
        setup({ CUSTOM_SERVICE_LAYER_HEADERS: { 'accept-language': i18n.language } });

        return serviceCall(PlanService, method, argsArray);
    }

    static customReportsService(method: string, argsArray: unknown[]) {
        setup({ CUSTOM_SERVICE_LAYER_HEADERS: { 'accept-language': i18n.language } });

        return serviceCall(CustomReportsService, method, argsArray);
    }

    static customInvestmentService(method: string, argsArray: unknown[]) {
        setup({ CUSTOM_SERVICE_LAYER_HEADERS: { 'accept-language': i18n.language } });

        return serviceCall(CustomInvestmentService, method, argsArray);
    }

    static customCommonService(method: string, argsArray: unknown[]) {
        setup({ CUSTOM_SERVICE_LAYER_HEADERS: { 'accept-language': i18n.language } });

        return serviceCall(CustomCommonService, method, argsArray);
    }

    static customAdvisoryServices(method: string, argsArray: unknown[]) {
        setup({ CUSTOM_SERVICE_LAYER_HEADERS: { 'accept-language': i18n.language } });

        return serviceCall(CustomAdvisoryServices, method, argsArray);
    }

    static customCaseManagementServices(method: string, argsArray: unknown[]) {
        setup({ CUSTOM_SERVICE_LAYER_HEADERS: { 'accept-language': i18n.language } });

        return serviceCall(CustomCaseManagementServices, method, argsArray);
    }

    static customSystemMessageService(method: string, argsArray: unknown[]) {
        setup({ CUSTOM_SERVICE_LAYER_HEADERS: { 'accept-language': i18n.language } });

        return serviceCall(CustomSystemMessageServices, method, argsArray);
    }

    static clientService(method: string, argsArray: unknown[]) {
        setup({ CLIENT_SERVICE_HEADERS: { 'accept-language': i18n.language } });

        return serviceCall(clientService, method, argsArray);
    }

    static contactInteraction(method: string, argsArray: Record<string, unknown>[]) {
        if (method === 'postInteractions') {
            const params = argsArray[0] || {};

            params.FallbackLanguageId = params.FallbackLanguageId || 2;
        }
        if (method === 'patchInteractions') {
            const params = argsArray[1] || {};

            params.FallbackLanguageId = params.FallbackLanguageId || 2;
        }

        return serviceCall(contactInteraction, method, argsArray);
    }
}

export default ServiceManager;
